// src/pages/FamilyDashboardPage/FamilyDashboardPage.jsx
import React, { useContext, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Import toast styles
import useAxios from "../../../hooks/useAxios";
import "./FamilyDashboardPage.css";
import { 
    Box, 
    CircularProgress, 
    Container, 
    Typography, 
    Button, 
    useMediaQuery, 
    Grid, 
    Tooltip,
    IconButton,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";
import Header from "../../../components/Header/Header";
import QuizCard from "../../../components/QuizComponents/Cards/QuizCard/QuizCard";
import ActivateAccountAlert from "../../../components/Auth/ActivateAccountAlert/ActivateAccountAlert";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import StudentAddQuizModal from "../../../components/DashboardComponents/Modals/StudentAddQuizModal/StudentAddQuizModal";
import { useTheme } from "@mui/material/styles";
import StudentReportTable from "../../../components/DashboardComponents/StudentDashboard/StudentReportTable/StudentReportTable";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import ConfirmationModal from "../../../components/common/ConfirmationModal/ConfirmationModal";
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupIcon from '@mui/icons-material/Group';
import ReportCard from "../../../components/Reporting/Cards/ReportCard";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GradeBookTable from "../../../components/Reporting/GradeBookTable/GradeBookTable";
import ReportModal from "../../../components/Reporting/Modals/ReportModal/ReportModal";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import QuizManagementTable from "../../../components/DashboardComponents/StudentDashboard/QuizManagementTable/QuizManagementTable";

const navLinks = [
];

function FamilyDashboardPage() {
    const axiosInstance = useAxios();
    const theme = useTheme();
    const { status } = useContext(AuthContext);
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isActivateAlertOpen, setIsActivateAlertOpen] = useState(false);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const [showAllNew, setShowAllNew] = useState(false);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [classData, setClassData] = useState(null);
    const [title, setTitle] = useState('Copy ID');
    const [studentsData, setStudentsData] = useState({});
    const [quizzesForTable, setQuizzesForTable] = useState([]);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [currentReport, setCurrentReport] = useState(null);
    
    // Comprehensive Reports State Variables
    const [classComprehensiveReports, setClassComprehensiveReports] = useState([]);
    const [loadingComprehensiveReports, setLoadingComprehensiveReports] = useState(false);
    const [generatingComprehensiveReport, setGeneratingComprehensiveReport] = useState(false);
    const [selectedComprehensiveReport, setSelectedComprehensiveReport] = useState(null);
    const [showComprehensiveReportDetails, setShowComprehensiveReportDetails] = useState(false);
    const [deleteReportModalOpen, setDeleteReportModalOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [deletingReport, setDeletingReport] = useState(false);
    
    // Determine initial display count based on screen size 
    let initialDisplayCount = 3;
    if (isSmallScreen) {
        initialDisplayCount = 1;
    } else if (isMediumScreen) {
        initialDisplayCount = 2;
    }

    const fetchQuizzes = async () => {
        try {
            const response = await axiosInstance.get("/quizzes/owner");
            if (response.data.success) {
                // Sort quizzes by updated_at in descending order
                const sortedQuizzes = response.data.quizzes.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
                setQuizzes(sortedQuizzes);
            } else {
                // setError(response.data.message || "Failed to fetch quizzes.");
                toast.error(response.data.message || "Failed to fetch quizzes.");
            }
        } catch (err) {
            // setError(err.response?.data?.message || "An unexpected error occurred.");
            toast.error(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const fetchClassData = async () => {
        try {
            const response = await axiosInstance.get("/classes/family");
            if (response.data.success) {
                // Sort quizzes by updated_at in descending order
                setClassData(response.data.class);
            } else {
                // setError(response.data.message || "Failed to fetch class data.");
                toast.error(response.data.message || "Failed to fetch class data.");
            }
        } catch (err) {
            // setError(err.response?.data?.message || "An unexpected error occurred.");
            navigate('/setup-profile');
            toast.error(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    }

    const prepareGradeBookData = (classObject) => {
        if (!classObject || !classObject.quizzes) return;
        
        const quizzesArray = classObject.quizzes; // an array of { id, title, sessions[] }
      
        // Build the data structure
        const tempStudentsData = {};
        
        quizzesArray.forEach((quiz) => {
          quiz.sessions.forEach((session) => {
            const user = session.user;
            // only if user.role === 'student' or you want to show for teachers too
            const userId = user.id;
            const fullName = user.student_profile
              ? user.student_profile.full_name
              : user.teacher_profile
              ? user.teacher_profile.full_name
              : user.profile
              ? user.profile.full_name
              : user.username;
      
            if (!tempStudentsData[userId]) {
              tempStudentsData[userId] = {
                id: userId,
                name: fullName,
                quizzes: {}
              };
            }
      
            if (!tempStudentsData[userId].quizzes[quiz.id]) {
              tempStudentsData[userId].quizzes[quiz.id] = [];
            }
      
            tempStudentsData[userId].quizzes[quiz.id].push({
              attempt: session.attempt,
              final_evaluation: session.final_evaluation,
              session_id: session.id
            });
          });
        });
      
        // quizzesForTable can just be id + title
        const quizList = quizzesArray.map(q => ({
          id: q.id,
          title: q.title,
        }));
      
        setStudentsData(tempStudentsData);
        setQuizzesForTable(quizList);
      };

    // Comprehensive Reports Functions
    const fetchClassComprehensiveReports = async () => {
        try {
            setLoadingComprehensiveReports(true);
            const response = await axiosInstance.get(`/reporting/classes/${classData.id}/comprehensive-report`);
            if (response.data.success) {
                // Handle both single report and array responses
                const reports = response.data.report ? [response.data.report] : response.data.reports || [];
                setClassComprehensiveReports(reports);
            } else {
                toast.error(response.data.message || "Failed to fetch class comprehensive reports.");
            }
        } catch (err) {
            // If no reports exist yet, that's fine - just set empty array
            if (err.response?.status === 404) {
                setClassComprehensiveReports([]);
            } else {
                toast.error(err.response?.data?.message || "An unexpected error occurred while fetching reports.");
            }
        } finally {
            setLoadingComprehensiveReports(false);
        }
    };

    const generateClassComprehensiveReport = async () => {
        try {
            setGeneratingComprehensiveReport(true);
            const response = await axiosInstance.post(`/ai-reports/classes/${classData.id}/generate-comprehensive-report`);
            if (response.data.success) {
                toast.success('Class comprehensive report generated successfully!');
                // Refresh the reports list
                await fetchClassComprehensiveReports();
            } else {
                toast.error(response.data.message || "Failed to generate class comprehensive report.");
            }
        } catch (err) {
            // Handle specific error cases with user-friendly messages
            if (err.response?.status === 404) {
                const errorMessage = err.response.data?.message || 'Class has no completed sessions to analyze.';
                toast.error(`Cannot generate report: ${errorMessage}`);
            } else if (err.response?.status === 403) {
                toast.error('You are not authorized to generate reports for this class.');
            } else if (err.response?.status >= 500) {
                toast.error('Server error occurred while generating the report. Please try again later.');
            } else {
                toast.error(err.response?.data?.message || "An unexpected error occurred while generating the report.");
            }
        } finally {
            setGeneratingComprehensiveReport(false);
        }
    };

    const handleViewComprehensiveReport = (report) => {
        setSelectedComprehensiveReport(report);
        setShowComprehensiveReportDetails(true);
    };

    const handleBackToComprehensiveReportsList = () => {
        setShowComprehensiveReportDetails(false);
        setSelectedComprehensiveReport(null);
    };

    // Handle delete report
    const handleDeleteReport = (report) => {
        setReportToDelete(report);
        setDeleteReportModalOpen(true);
    };

    const handleCloseDeleteReportModal = () => {
        setDeleteReportModalOpen(false);
        setReportToDelete(null);
    };

    const handleConfirmDeleteReport = async () => {
        if (!reportToDelete) return;
        
        setDeletingReport(true);
        try {
            const response = await axiosInstance.delete(`/reporting/classes/${classData.id}/comprehensive-report/${reportToDelete.id}`);
            if (response.data.success) {
                toast.success('Report deleted successfully!');
                // Remove the deleted report from the list
                setClassComprehensiveReports(prev => prev.filter(r => r.id !== reportToDelete.id));
                handleCloseDeleteReportModal();
            } else {
                toast.error(response.data.message || 'Failed to delete report.');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'An error occurred while deleting the report.');
        } finally {
            setDeletingReport(false);
        }
    };

    useEffect(() => {
        fetchClassData();
        fetchQuizzes();
    }, []);

    useEffect(() => {
        if (classData) {
          prepareGradeBookData(classData);
          fetchClassComprehensiveReports();
        }
      }, [classData]);

    const handleEditQuiz = (quizId) => {
        if(status === 'trial') {
            setIsActivateAlertOpen(true);
        } else {
            navigate(`/quiz-review/${quizId}`);
        }
    };

    const handleDeleteQuiz = async (quizId) => {
        if (status === 'trial') {
            setIsActivateAlertOpen(true);
        } else {
            setSelectedQuiz(quizId);
            setIsConfirmationModalOpen(true); 
        }
    };

    const handleCloseConfirmationModal = () => {
        setIsConfirmationModalOpen(false);
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.delete(`/quizzes/${selectedQuiz}`);
            if (response.data.success) {
                // Update quizzes after deletion
                fetchQuizzes();
                handleCloseConfirmationModal();
            } else {
                setError(response.data.message || "Failed to delete quiz.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateQuiz = () => {
        // navigate('/quiz/create');
        navigate('/quiz/create', { state: { classId: classData.id } })
    };

    const reloadPage = () => {
        window.location.reload(); // Reload the page
    };

    // Determine which quizzes to show
    const displayedQuizzes = showAllNew ? quizzes : quizzes.slice(0, initialDisplayCount);

    const totalQuizzes = quizzes.length;

    const isSubsetNew = !showAllNew && totalQuizzes > displayedQuizzes.length;

    // Compute displayed name from user object
    const getUserDisplayName = (user) => {
        if (user.role === 'student' && user.student_profile) {
            return user.student_profile.full_name;
        } else if (user.family_profile) {
            return user.family_profile.family_name;
        }
        return user.username;
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(classData?.unique_identifier || "");
        setTitle('Copied!');
      };

      const handleViewReportClick = async (sessionId) => {
        try {
          const response = await axiosInstance.get(`/sessions/${sessionId}/report`);
          if (response.data.success) {
            setCurrentReport(response.data.report);
            setReportModalOpen(true);
          } else {
            // setError(response.data.message || "Failed to fetch report.");
            toast.error(response.data.message || "Failed to fetch report.");
          }
        } catch (err) {
        //   setError(err.response?.data?.message || "An unexpected error occurred.");
        toast.error(err.response?.data?.message || "Report not found or an unexpected error occurred.");
        }
      };

    let content;
    if (loading) {
        content = (
            <Box className="loading-container">
                <CircularProgress />
                <Typography variant="h6" className="loading-text">
                    Fetching Assessments...
                </Typography>
            </Box>
        );
    } else if (error) {
        content = (
            <Box className="error-container">
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    } else {
        content = (
            <>
            {/* Class Info Section */}
            <Box className="class-info-card ">
                <Box className="class-info-header">
                    <Typography variant="h5" className="class-title">
                    {classData?.class_name}
                    </Typography>
                    <Box className="class-id-section">
                    <Typography variant="body2" className="class-id-title">
                        Class ID <br /> <span className="class-id-value">{classData?.unique_identifier}</span> 
                    </Typography>
                    <Tooltip title={classData?.title}>
                        <IconButton onClick={handleCopy}>
                        <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                    </Box>
                </Box>
                <Typography variant="body1" className="class-description">
                    {classData?.description || "No description provided."}
                </Typography>
                <Typography className="family-support-message">
                    if you need any help, there is a dedicated support person available to help you
                </Typography>
                <Typography className="family-support-message">
                   <WhatsAppIcon /> +971 (0) 509910248
                </Typography>
            </Box>

                {/* New Quizzes Section */}
                <ContentHeader
                    title="Your Assessments"
                    buttonLabel="Create Assessment"
                    onButtonClick={handleCreateQuiz}
                />
                <div className="quizzes-grid my-5">
                    {displayedQuizzes.length !== 0 ? (
                        // displayedQuizzes.map((quiz) => (
                        //     <QuizCard
                        //         key={quiz.id}
                        //         quiz={quiz}
                        //         onEditClick={() => handleEditQuiz(quiz.id)}
                        //         onDeleteClick={() => handleDeleteQuiz(quiz.id)}
                        //         onReload={reloadPage}
                        //     />
                        // ))

                    <div className="mb-5">
                        <QuizManagementTable />
                    </div>
                    ) : (
                        <Typography className="mb-5">No Assessments to show</Typography>
                    )}
                </div>
                {(isSubsetNew || (showAllNew && totalQuizzes > initialDisplayCount)) && (
                    <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                        <Typography variant="body2" sx={{ mr: 2 }} className="mb-5">
                            {showAllNew
                                ? `Showing all ${totalQuizzes} Assessments`
                                : `Showing ${displayedQuizzes.length} of ${totalQuizzes} Assessments`}
                        </Typography>
                        <Button
                            className="text-button mb-5"
                            onClick={() => setShowAllNew(!showAllNew)}
                        >
                            {showAllNew ? "View Less" : "View All"}
                        </Button>
                    </Box>
                )}
                {/* Reporting Section */}
                <ContentHeader
                    title="Reporting"
                />
                <div className="reports-grid mb-5">
                    {/* Active Sessions Report Card */}
                    <ReportCard
                        icon={AssessmentIcon}
                        title="Active Sessions"
                        description="Monitor all active sessions of students currently taking your Assessments. Stay updated with their real-time progress and performance."
                        buttonLabel="View Active Sessions"
                        onButtonClick={() => navigate('/reporting/active-sessions')}
                    />
                    {/* Attempted Users Report Card */}
                    <ReportCard
                        icon={GroupIcon}
                        title="Attempted Users"
                        description="View comprehensive reports of all students who have attempted your Assessments. Analyze their performance and insights."
                        buttonLabel="View Attempted Users"
                        onButtonClick={() => navigate('/reporting/attempted-users')}
                    />
                </div>
                {/* Grade Book */}
                <ContentHeader
                    title="Grade Book"
                />

                <GradeBookTable
                quizzes={quizzesForTable}
                studentsData={studentsData}
                onViewReport={handleViewReportClick}
                />

                {/* Class Comprehensive Reports Section */}
                <Box className="mt-5">
                    <Box className="content-header">
                        <Typography variant="h4" className="content-header-title">
                            Class Reporting
                        </Typography>
                        <Box className="content-header-buttons">
                            <Button 
                                className="content-header-button primary-button" 
                                onClick={generateClassComprehensiveReport}
                                disabled={generatingComprehensiveReport}
                                startIcon={generatingComprehensiveReport ? <CircularProgress size={20} /> : <AssessmentIcon />}
                                variant="contained"
                            >
                                {generatingComprehensiveReport ? "Generating Report..." : "Generate New Report"}
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Box className="comprehensive-reports-section">
                    {/* Reports Table */}
                    {loadingComprehensiveReports ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : classComprehensiveReports.length > 0 ? (
                        <TableContainer component={Paper} elevation={1} className="mb-5">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Generated Date</TableCell>
                                        <TableCell>Total Sessions</TableCell>
                                        <TableCell>Total Students</TableCell>
                                        <TableCell>Total Quizzes</TableCell>
                                        <TableCell>Class Performance</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {classComprehensiveReports.map((report) => (
                                        <TableRow key={report.id}>
                                            <TableCell>
                                                {new Date(report.created_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>{report.total_sessions}</TableCell>
                                            <TableCell>{report.total_students}</TableCell>
                                            <TableCell>{report.total_quizzes}</TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color={
                                                        report.class_performance > 75
                                                            ? "success.main"
                                                            : report.class_performance > 50
                                                            ? "warning.main"
                                                            : "error.main"
                                                    }
                                                    fontWeight={500}
                                                >
                                                    {report.class_performance}%
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="View Report Details" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewComprehensiveReport(report)}
                                                        color="primary"
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Report" arrow>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteReport(report)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box textAlign="center" py={4}>
                            <Typography variant="body1" color="textSecondary">
                                No comprehensive reports generated yet.
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mt={1}>
                                Click "Generate New Comprehensive Report" to create the first report for this class.
                            </Typography>
                        </Box>
                    )}
                </Box>

                <ReportModal
                open={reportModalOpen}
                onClose={() => setReportModalOpen(false)}
                report={currentReport}
                />

                {/* Comprehensive Report Modal */}
                {selectedComprehensiveReport && (
                    <Dialog 
                        open={showComprehensiveReportDetails} 
                        onClose={handleBackToComprehensiveReportsList}
                        fullWidth 
                        maxWidth="md"
                    >
                        <DialogTitle className="student-report-modal-title">
                            <Box display="flex" alignItems="center">
                                {/* Left side: Back button and title */}
                                <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
                                    <Box display="flex" alignItems="center" gap={1} className="report-modal-back-button">
                                        <Typography variant="h6">
                                            Class Comprehensive Report
                                        </Typography>
                                    </Box>
                                </Box>
                                {/* Right side: Close button */}
                                <Box>
                                    <Button onClick={handleBackToComprehensiveReportsList} className="student-report-modal-close-button">
                                        <CloseIcon />
                                    </Button>
                                </Box>
                            </Box>
                        </DialogTitle>
                        
                        <DialogContent className="student-report-modal-content">
                            {selectedComprehensiveReport && (
                                <>
                                    {/* Report Summary */}
                                    <Box className="student-info-section" mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                                        {/* Avatar or Icon */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    borderRadius: '50%',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontSize: 28,
                                                    fontWeight: 700,
                                                    mr: 2,
                                                }}
                                            >
                                                {classData?.class_name?.charAt(0).toUpperCase() || 'C'}
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" fontWeight={600}>
                                                    {classData?.class_name || 'Class'}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Class ID: <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{classData?.unique_identifier || 'N/A'}</span>
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {/* Info Stats */}
                                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Total Sessions
                                                </Typography>
                                                <Typography variant="h6" fontWeight={700} color="primary.main">
                                                    {selectedComprehensiveReport.total_sessions}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Total Students
                                                </Typography>
                                                <Typography variant="h6" fontWeight={700} color="primary.main">
                                                    {selectedComprehensiveReport.total_students}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Total Quizzes
                                                </Typography>
                                                <Typography variant="h6" fontWeight={700} color="primary.main">
                                                    {selectedComprehensiveReport.total_quizzes}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Class Performance
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    fontWeight={700}
                                                    color={
                                                        selectedComprehensiveReport.class_performance > 75
                                                            ? "success.main"
                                                            : selectedComprehensiveReport.class_performance > 50
                                                            ? "warning.main"
                                                            : "error.main"
                                                    }
                                                >
                                                    {selectedComprehensiveReport.class_performance}%
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Report Content */}
                                    <Box className="report-content-section">
                                        {/* Strength Points */}
                                        <Box className="report-section" mb={3}>
                                            <Typography variant="subtitle1" className="report-label" gutterBottom>
                                                Strength Points:
                                            </Typography>
                                            <Typography variant="body1" className="report-text">
                                                {selectedComprehensiveReport.strength_points || "N/A"}
                                            </Typography>
                                        </Box>

                                        {/* Points to Improve */}
                                        <Box className="report-section" mb={3}>
                                            <Typography variant="subtitle1" className="report-label" gutterBottom>
                                                Points to Improve:
                                            </Typography>
                                            <Typography variant="body1" className="report-text">
                                                {selectedComprehensiveReport.points_to_improve || "N/A"}
                                            </Typography>
                                        </Box>

                                        {/* Recommendation */}
                                        <Box className="report-section">
                                            <Typography variant="subtitle1" className="report-label" gutterBottom>
                                                Recommendation:
                                            </Typography>
                                            <Typography variant="body1" className="report-text">
                                                {selectedComprehensiveReport.recommendation || "N/A"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </DialogContent>
                    </Dialog>
                )}

                {/* Delete Report Confirmation Modal */}
                <ConfirmationModal
                    open={deleteReportModalOpen}
                    onClose={handleCloseDeleteReportModal}
                    isDanger={true}
                    title="Delete Comprehensive Report"
                    subtitle={`Are you sure you want to delete this report?`}
                    onSubmit={handleConfirmDeleteReport}
                    submitText={deletingReport ? "Deleting..." : "Delete Report"}
                    hasNotes={false}
                >
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        This action cannot be undone. The report will be permanently removed.
                    </Typography>
                </ConfirmationModal>
            </>
        );
    }

    return (
        <>
            <Header navLinks={navLinks} isScrollEffect={false} />
            <Container className="owner-quizzes-container page-container">
                {content}
                <ActivateAccountAlert
                    open={isActivateAlertOpen}
                    onClose={() => setIsActivateAlertOpen(false)}
                />
            </Container>
            <ConfirmationModal
                open={isConfirmationModalOpen}
                onClose={handleCloseConfirmationModal}
                isDanger={true}
                title="Are you sure you want to delete this Assessment?"
                onSubmit={handleDelete}
                hasNotes={false}
            />
            <ToastContainer position="top-right" autoClose={5000} />
        </>
    );
}

export default FamilyDashboardPage;
